import React from 'react';
import styles from './AGGroupedSelect.module.css';
import InfoTooltip from 'components/Dashboard/SpotCard/InfoTooltip/InfoTooltip';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';

export type GroupedSelectItem = {
    value: string;
    label: string;
    color: string;
    subLabel?: string;
    tooltip?: string;
};

export type SelectGroup = {
    groupLabel: string;
    items: GroupedSelectItem[];
};

type AGGroupedSelectProps = {
    groups: SelectGroup[];
    selectedValues: string[];
    onSelectionChange: (values: string[], clickedItem: string, offOn: 'Off' | 'On') => void;
    loading?: boolean;
};

function AGGroupedSelect({ groups, selectedValues, onSelectionChange, loading = false }: AGGroupedSelectProps) {
    const itemCount = groups.reduce((total, group) => total + group.items.length, 0);

    const isItemSelected = (value: string) => selectedValues.includes(value);

    const isGroupFullySelected = (group: SelectGroup) =>
        group.items.every((item) => selectedValues.includes(item.value));

    const handleItemToggle = (value: string) => {
        if (isItemSelected(value)) {
            onSelectionChange(
                selectedValues.filter((v) => v !== value),
                value,
                'Off'
            );
        } else {
            onSelectionChange([...selectedValues, value], value, 'On');
        }
    };

    const handleGroupToggle = (group: SelectGroup) => {
        const groupValues = group.items.map((item) => item.value);
        const isFullySelected = isGroupFullySelected(group);

        if (isFullySelected) {
            onSelectionChange(
                selectedValues.filter((v) => !groupValues.includes(v)),
                group.groupLabel,
                'Off'
            );
        } else {
            const newValues = [...selectedValues];
            groupValues.forEach((v) => {
                if (!newValues.includes(v)) {
                    newValues.push(v);
                }
            });
            onSelectionChange(newValues, group.groupLabel, 'On');
        }
    };

    if (loading) {
        return (
            <div className={styles.skeletonContainer}>
                {Array.from({ length: itemCount }).map((_, i) => (
                    <div key={i} className={styles.skeletonItem}>
                        <RectangleSkeleton />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {groups.map((group) => (
                <div key={group.groupLabel} className={styles.group} onClick={() => handleGroupToggle(group)}>
                    <div className={styles.groupHeader}>
                        <span className={styles.groupLabel}>{group.groupLabel}</span>
                        <input
                            type="checkbox"
                            checked={isGroupFullySelected(group)}
                            onChange={() => handleGroupToggle(group)}
                            onClick={(e) => e.stopPropagation()}
                            className={styles.checkbox}
                        />
                    </div>
                    <div className={styles.itemsContainer} onClick={(e) => e.stopPropagation()}>
                        {group.items.map((item) => (
                            <label key={item.value} className={styles.item}>
                                <div className={styles.itemContent}>
                                    <div className={styles.itemHeader}>
                                        <span
                                            className={styles.colorIndicator}
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className={styles.itemLabel}>{item.label}</span>
                                        {item.tooltip && <InfoTooltip title={item.tooltip} />}
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={isItemSelected(item.value)}
                                        onChange={() => handleItemToggle(item.value)}
                                        className={styles.checkbox}
                                    />
                                </div>
                                {item.subLabel && <span className={styles.subLabel}>{item.subLabel}</span>}
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AGGroupedSelect;
